<template>
  <!-- eslint-disable  max-len -->
  <div class="group-management">
    <transition name="fade">
      <add-group-modal
        v-if="showAddGroupModal"
        :group="selectedGroup"
        :loading="loading"
        :update-mode="updateGroupMode"
        @submit="handleGroupMode"
        @close="toggleAddGroupModal"
      />
    </transition>
    <transition name="fade">
      <confirmation-modal
        v-if="showDeleteConfirmation"
        :confirmation-type="confirmation.type"
        :title="confirmation.title"
        :description="confirmation.description"
        :button-text="confirmation.button"
        @confirm="deleteGroup"
        @cancel="toggleDeleteConfirmation"
      />
    </transition>
    <div class="page-topbar mb-1">
      <div class="page-topbar__title">
        Group Management
      </div>
      <div v-tooltip="{ content: createToolTipMessage }">
        <button
          id="add-group-button"
          class="btn btn--raised"
          :disabled="!canCreateGroup"
          @click="toggleAddGroupModal"
        >
          Add Group
        </button>
      </div>
    </div>
    <div class="group-management-actionbar">
      <pagination
        :loading="loading"
        :page.sync="groupPaginationParams.page"
        :per-page.sync="groupPaginationParams.perPage"
        :total-rows.sync="totalGroupRows"
        :rows-per-page-label="'Groups per page'"
      />
    </div>
    <div class="group-management-container">
      <template v-if="groups.length > 0">
        <div class="group-management-table">
          <div class="group-management-table__item group-management-table__header">
            <span>Name</span>
            <span>Description</span>
            <span>Authenticator</span>
            <span class="group-management-table__item--right">Users</span>
            <span class="group-management-table__item--right">Devices</span>
            <span class="group-management-table__item--right"></span>
          </div>
          <template v-for="(group, index) in groups">
            <div
              :id="'group-item-' + index"
              :key="index"
              class="group-management-table__item"
              @click="selectGroup(group)"
            >
              <span>{{ group.name }}</span>
              <span>{{ group.description }}</span>
              <span>{{ authenticatorName(group) }}</span>
              <span class="group-management-table__item--right">
                {{ group.users.length }}
              </span>
              <span class="group-management-table__item--right">
                {{ group.devices.length }}
              </span>
              <span class="group-management-table__item--right">
                <div v-tooltip="{ content: deleteToolTipMessage }">
                  <button
                    id="delete-group"
                    class="btn__icon group-management-table__item-btn"
                    :disabled="!canDeleteGroup"
                    @click.stop="toggleDeleteConfirmation(group)"
                  >
                    <span v-html="deleteIcon" />
                  </button>
                </div>
              </span>
            </div>
          </template>
        </div>
      </template>
      <template v-else>
        <div
          id="empty-state-group"
          class="empty-state"
        >
          <template v-if="loading">
            Loading Groups...
          </template>
          <template v-else>
            No groups have been added or you do not have access to see current groups.
            <p>Groups can be used to create access rules by pairing users and devices.</p>
          </template>
        </div>
      </template>
    </div>
  </div>
  <!-- eslint-enable  max-len -->
</template>

<script>
import groupQueries from '@/graphql/groups.js'
import Pagination from '@/components/pagination.vue'
import AddGroupModal from '@/components/addGroupModal.vue'
import ConfirmationModal from '@/components/confirmationModal.vue'
import feather from 'feather-icons'
import constants from '@/global/constants.js'

export default {
  name: 'GroupManagement',
  components: {
    Pagination,
    AddGroupModal,
    ConfirmationModal,
  },
  mixins: [groupQueries],
  data() {
    return {
      loading: false,
      editMode: false,
      showAddGroupModal: false,
      updateGroupMode: false,
      showDeleteConfirmation: false,
      groups: [],
      selectedGroup: null,
      totalGroupRows: 0,
      groupPaginationParams: {
        page: 1,
        perPage: 10,
      },
      confirmation: {
        type: 'danger',
        title: 'Delete Group?',
        description:
          'WARNING: Deleting a group will remove all users, and devices in the group. Users will not have access to devices assigned to this group. Are you sure?',
        button: 'Delete Group',
      },
      deleteGroupId: null,
      deleteIcon: feather.icons['trash-2'].toSvg({width: '15px'}),
      canAddGroup: false,
      canDeleteGroup: false,
      actionNotAvailableMessage: constants.actionNotAvailableMessage,
    }
  },
  computed: {
    createToolTipMessage() {
      return this.canCreateGroup ? '' : this.actionNotAvailableMessage
    },
    deleteToolTipMessage() {
      return this.canDeleteGroup ? 'Delete Group' : this.actionNotAvailableMessage
    },
  },
  watch: {
    groupPaginationParams: {
      handler: function () {
        this.fetchGroups()
      },
      deep: true,
    },
  },
  created() {
    this.canCreateGroup = this.$can('create', 'group')
    this.canDeleteGroup = this.$can('delete', 'group')
    this.fetchGroups()
  },
  methods: {
    async fetchGroups() {
      this.loading = true
      try {
        const {groupList, totalRows} = await this.listGroupsQuery(this.groupPaginationParams)
        this.loading = false
        this.totalGroupRows = totalRows
        this.groups = groupList
      } catch (error) {
        this.loading = false
        this.displayAlert('fail', 'Unable to retrieve groups.')
        console.log('Fetch Groups Failed.', error)
      }
    },
    /*
     * Depending on if the user clicked an exisiting group from the list it will
     * be set to update mode, otherwise create a new group.
     */
    handleGroupMode(groupDetails) {
      this.loading = true
      if (this.updateGroupMode) {
        if (this.$can('update', 'group')) {
          this.handleUpdateGroup(groupDetails)
        } else if (this.$can('update', 'group-users')) {
          this.handleUpdateGroupUsers(groupDetails)
        } else {
          console.log('User dont have permissions')
        }
      } else {
        this.createGroup(groupDetails)
      }
    },
    async createGroup(groupDetails) {
      try {
        const _result = await this.createGroupMutation(groupDetails)
        groupDetails.uuid = _result
        await this.handleUpdateGroup(groupDetails)
        this.displayAlert('success', 'Group created.')
      } catch (error) {
        this.loading = false
        this.displayAlert('fail', 'Unable to retrieve groups.')
        console.log('Fetch Groups Failed.', error)
      }
    },
    /*
     * Backend only processes adding/deleteing users and devices in seperate
     * calls. Depending if the array of users or devices to be added or deleted
     * contains items, a request will be sent out
     */
    async handleUpdateGroup(groupDetails) {
      try {
        await this.updateGroupDetails(groupDetails)
        await this.updateGroupAddUsers(groupDetails)
        await this.updateGroupAddDevices(groupDetails)
        await this.updateGroupDeleteUsers(groupDetails)
        await this.updateGroupDeleteDevices(groupDetails)

        this.loading = false
        this.toggleAddGroupModal()
        this.fetchGroups()
      } catch (error) {
        console.log(error)
        this.loading = false
      }
    },
    async handleUpdateGroupUsers(groupDetails) {
      try {
        await this.updateGroupAddUsers(groupDetails)
        await this.updateGroupDeleteUsers(groupDetails)

        this.loading = false
        this.toggleAddGroupModal()
        this.fetchGroups()
      } catch (error) {
        console.log(error)
        this.loading = false
      }
    },
    /*
     * Update the name and description of a group.
     */
    async updateGroupDetails(groupDetails) {
      try {
        return await this.updateGroupDetailsMutation(groupDetails)
      } catch (error) {
        console.log(error)
        this.displayAlert('fail', 'Unable to update group.')
      }
    },
    /*
     * Update and add users to a group.
     */
    async updateGroupAddUsers(groupDetails) {
      const {uuid, addUsers} = groupDetails
      if (addUsers.length > 0) {
        try {
          return await this.updateGroupAddUsersMutation(uuid, addUsers)
        } catch (error) {
          this.displayAlert('fail', 'Unable to update group.')
        }
      }
    },
    /*
     * Update and add devices to a group.
     */
    async updateGroupAddDevices(groupDetails) {
      const {uuid, addDevices} = groupDetails
      if (addDevices.length > 0) {
        try {
          return await this.updateGroupAddDevicesMutation(uuid, addDevices)
        } catch (error) {
          this.displayAlert('fail', 'Unable to update group.')
        }
      }
    },
    /*
     * Update and remove users from a group.
     */
    async updateGroupDeleteUsers(groupDetails) {
      const {uuid, deleteUsers} = groupDetails
      if (deleteUsers.length > 0) {
        try {
          return await this.updateGroupDeleteUsersMutation(uuid, deleteUsers)
        } catch (error) {
          this.displayAlert('fail', 'Unable to update group.')
        }
      }
    },
    /*
     * Update and remove devices from a group.
     */
    async updateGroupDeleteDevices(groupDetails) {
      const {uuid, deleteDevices} = groupDetails
      if (deleteDevices.length > 0) {
        try {
          return await this.updateGroupDeleteDevicesMutation(uuid, deleteDevices)
        } catch (error) {
          this.displayAlert('fail', 'Unable to update group.')
        }
      }
    },
    async deleteGroup() {
      if (!this.canDeleteGroup) {
        return
      }
      this.showDeleteConfirmation = false
      this.loading = true
      try {
        await this.deleteGroupMutation(this.deleteGroupId)
        this.loading = false
        this.fetchGroups()
        this.displayAlert('success', 'Group successfully deleted.')
      } catch (error) {
        this.loading = false
        this.displayAlert('fail', 'Unable to delete group.')
      }
    },
    authenticatorName(group) {
      const {authenticator, credential} = group
      const _name = {
        sms: 'SMS Authenticator',
        none: 'Bypass',
      }
      if (credential) {
        return `${credential.name} (${credential.type})`
      }
      return _name[authenticator]
        ? `${_name[authenticator]} (${authenticator})`
        : 'No Authenticator'
    },
    /*
     * When closing the modal, set the updateMode back to false.
     */
    toggleAddGroupModal() {
      this.showAddGroupModal = !this.showAddGroupModal
      if (!this.showAddGroupModal) {
        this.updateGroupMode = false
      }
    },
    toggleDeleteConfirmation(group) {
      if (!this.canDeleteGroup) {
        return
      }
      this.deleteGroupId = group
      this.showDeleteConfirmation = !this.showDeleteConfirmation
    },
    /*
     * When a group is selected from the list, update the modal to be in update
     * mode.
     */
    selectGroup(selectedGroup) {
      this.updateGroupMode = true
      this.selectedGroup = selectedGroup
      this.toggleAddGroupModal()
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/scss/_tables.scss";

.group-management {
  &-actionbar {
    z-index: 10;
    background: $white;
    position: sticky;
    top: 0;
    padding: 1em 0;
  }
  &-table {
    width: 100%;
    &__item {
      @include grid-table-item();
      grid-template-columns: 1fr 1fr 1fr 0.5fr 0.5fr 0.5fr;
      @media #{$large-display} {
        grid-template-columns: 1fr 2fr 1fr 0.5fr 0.5fr 0.5fr;
      }
    }
    &__header {
      @include grid-table-header();
    }
  }
  &-container {
    @include flex-display();
    @include flex-justify-content(flex-start);
    flex-wrap: wrap;
    @media #{$mobile} {
      @include flex-direction(column);
      @include flex-justify-content(center);
    }
  }
  &__loader {
    width: 100%;
    height: 30vh;
    background: $off-white;
    border-radius: 5px;
    @include flex-display();
    @include flex-justify-content(center);
    @include flex-align-items(center);
  }
}
</style>
